// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A visually engaging loading component for exception reports.
 *
 * Displays a checklist of analysis steps that get "checked off" sequentially
 * while waiting for the API response. Provides users with feedback that
 * analysis work is happening rather than showing a simple spinner.
 *
 * Timing is dynamically calculated from the organization's asset count via
 * the report loader configuration. Steps complete at timed intervals
 * proportional to expected load time, and accelerate to completion once
 * actual data arrives.
 */
import {
  type ExceptionReportType,
  ReportLoaderDisplay,
  exceptionReportConfigs,
  useReportLoaderProgress,
} from '@/report/components/report-loader';

interface Props {
  /** When true, accelerates remaining steps and completes quickly */
  isDataReady?: boolean;
  /** Called when all steps are complete */
  onComplete?: () => void;
  /** Type of report being loaded (affects step labels) */
  reportType: ExceptionReportType;
}

/**
 * Displays a pseudo-progress loader with a checklist of analysis steps.
 * Fetches asset count to calculate realistic step timing, then animates
 * through steps proportionally. Accelerates when actual data arrives.
 */
export function ExceptionReportLoader(props: Props) {
  const { isDataReady, onComplete, reportType } = props;

  const config = exceptionReportConfigs[reportType];

  const progress = useReportLoaderProgress({
    config,
    isDataReady,
    onComplete,
  });

  return (
    <ReportLoaderDisplay
      currentStep={progress.currentStep}
      completedSteps={progress.completedSteps}
      stepLabels={config.stepLabels}
    />
  );
}
